import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatError } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Clock } from "lucide-react";

const LABELS = {
  lead_unlock: "Lead unlock",
  subscription_pro: "Contractor Pro plan",
  subscription_prem: "Contractor Premium plan",
  customer_deposit: "Booking deposit",
  featured_suburb: "Featured suburb",
};

export default function PayResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [txn, setTxn] = useState(null);
  const [state, setState] = useState("checking"); // checking | paid | pending | error | expired
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (state !== "paid" || !txn || trackedRef.current) return;
    trackedRef.current = true;
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      const value = Number(txn.amount) || 0;
      const currency = (txn.currency || "aud").toUpperCase();
      const contentName = LABELS[txn.purpose] || txn.purpose || "Payment";
      window.fbq("track", "Purchase", {
        value,
        currency,
        content_name: contentName,
        content_ids: [txn.purpose],
        content_type: "product",
      });
    }
  }, [state, txn]);

  useEffect(() => {
    if (!sessionId) { setState("error"); setError("Missing session_id"); return; }
    let cancelled = false;
    attemptsRef.current = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTxn(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("expired"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 6) { setState("pending"); return; }
        setTimeout(poll, 2500);
      } catch (err) {
        if (cancelled) return;
        setError(formatError(err));
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // sessionId is the only real trigger — the setters and helpers are stable module refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const successCopy = txn && {
    lead_unlock: {
      title: "Lead unlocked",
      body: "The lead contact details have been unlocked for your business. Check your email — we'll be in touch with the full lead information.",
    },
    subscription_pro: {
      title: "Welcome to Pro",
      body: "Your Contractor Pro plan is active for 30 days. Priority lead matching is now enabled for your service areas.",
    },
    subscription_prem: {
      title: "Welcome to Premium",
      body: "Your Premium plan is active for 30 days. You'll get first-look access on new leads and featured rotation slots.",
    },
    customer_deposit: {
      title: "Deposit received",
      body: "Your booking deposit has been recorded. The matched contractor will confirm the clean-up schedule directly with you.",
    },
    featured_suburb: {
      title: "You're featured",
      body: `Your business is now featured for the next 30 days${txn.context?.suburb ? ` in ${txn.context.suburb.replace(/-/g, " ")}` : ""}. New leads in that area will be offered to you first.`,
    },
  }[txn.purpose];

  return (
    <PublicLayout>
      <Seo title="Payment result | Site Cleanup" />
      <section className="py-20 md:py-28">
        <div className="rs-container max-w-2xl">
          {state === "checking" && (
            <div className="text-center" data-testid="pay-checking">
              <Loader2 className="animate-spin mx-auto text-safety" size={48} />
              <h1 className="mt-6 font-display text-3xl md:text-4xl uppercase">Confirming your payment…</h1>
              <p className="mt-2 text-muted-foreground">This usually takes a few seconds.</p>
            </div>
          )}

          {state === "paid" && successCopy && (
            <div className="text-center" data-testid="pay-success">
              <CheckCircle2 className="mx-auto text-safety" size={56} />
              <span className="rs-eyebrow mt-6 block">Payment successful</span>
              <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">{successCopy.title}</h1>
              <p className="mt-4 text-muted-foreground max-w-lg mx-auto">{successCopy.body}</p>
              <div className="mt-6 bg-white border border-border p-5 max-w-md mx-auto text-left text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Item</span><span className="font-semibold">{LABELS[txn.purpose] || txn.purpose}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-semibold">A${Number(txn.amount).toFixed(2)} {txn.currency.toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-mono text-xs">{txn.session_id.slice(-14)}</span></div>
              </div>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to="/" className="rs-btn-outline">Back to home</Link>
                <Link to="/pricing" className="rs-btn-primary">See all plans <ArrowRight size={16} className="ml-2"/></Link>
              </div>
            </div>
          )}

          {state === "pending" && (
            <div className="text-center" data-testid="pay-pending">
              <Clock className="mx-auto text-safety" size={48} />
              <h1 className="mt-6 font-display text-3xl uppercase">Payment still processing</h1>
              <p className="mt-2 text-muted-foreground">We haven't received final confirmation yet. Your card should be charged shortly — you can safely close this tab; we'll email you once it clears.</p>
              <Link to="/" className="rs-btn-outline mt-8">Back to home</Link>
            </div>
          )}

          {state === "expired" && (
            <div className="text-center" data-testid="pay-expired">
              <XCircle className="mx-auto text-destructive" size={48} />
              <h1 className="mt-6 font-display text-3xl uppercase">Session expired</h1>
              <p className="mt-2 text-muted-foreground">This payment session has expired. Please start again.</p>
              <Link to="/pricing" className="rs-btn-primary mt-8">Try again</Link>
            </div>
          )}

          {state === "error" && (
            <div className="text-center" data-testid="pay-error">
              <XCircle className="mx-auto text-destructive" size={48} />
              <h1 className="mt-6 font-display text-3xl uppercase">Couldn't confirm your payment</h1>
              <p className="mt-2 text-muted-foreground">{error}</p>
              <Link to="/pricing" className="rs-btn-outline mt-8">Back to pricing</Link>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
